import {
    EuiButtonEmpty,
    EuiButtonIcon,
    EuiComboBox,
    EuiFlexGrid,
    EuiFlexGroup,
    EuiFlexItem,
    EuiSpacer,
} from "@elastic/eui";
import { EuiComboBoxOptionOption } from "@elastic/eui/src/components/combo_box/types";
import React, { useState } from "react";

function InlineIconEditor({
    itemType,
    isLoading,
    initialOptions,
    options,
    onSave,
    renderValue,
    readonly = false,
}: {
    itemType: string;
    isLoading: boolean;
    options: EuiComboBoxOptionOption<string>[];
    initialOptions: EuiComboBoxOptionOption<string>[];
    onSave: (value: string[]) => void;
    renderValue?: (value: string) => JSX.Element;
    readonly?: boolean;
}) {
    const [_editing, _setEditing] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState(initialOptions);

    const filteredOptions = options.filter((option) => !selectedOptions.map((o) => o.value).includes(option.value));

    const onChange = (selectedOptions: EuiComboBoxOptionOption<string>[]) => {
        setSelectedOptions(selectedOptions);
    };

    const save = () => {
        _setEditing(false);
        const newData = selectedOptions
            .map((option) => option.value)
            .filter((value): value is string => value !== undefined);
        onSave(newData);
    };

    const toggleEditing = () => {
        setSelectedOptions(initialOptions);
        if (readonly) {
            _setEditing(false);
        } else {
            _setEditing(!_editing);
        }
    };

    const editor = (
        <>
            <EuiFlexGroup>
                <EuiFlexItem>
                    <EuiComboBox
                        style={{ minWidth: 220 }}
                        aria-label="Flags editor"
                        placeholder={`Select ${itemType}s`}
                        options={filteredOptions} // Use the filtered options
                        selectedOptions={selectedOptions}
                        onChange={onChange}
                        isClearable={true}
                        data-test-subj="demoComboBox"
                        autoFocus
                    />
                </EuiFlexItem>
                <EuiFlexItem>
                    <EuiFlexItem grow={false}>
                        <EuiButtonIcon aria-label="save and stop" iconType="save" iconSize="l" onClick={save} />
                        <EuiSpacer size="m" />
                        <EuiButtonIcon
                            aria-label="stop without save"
                            iconType="editorUndo"
                            iconSize="l"
                            color="danger"
                            onClick={toggleEditing}
                        />
                    </EuiFlexItem>
                </EuiFlexItem>
            </EuiFlexGroup>
        </>
    );

    return (
        <div>
            {isLoading && <p>Loading...</p>}
            {!_editing && !isLoading && (
                <EuiFlexGroup onClick={toggleEditing} style={{ marginTop: "-0.4em", cursor: "pointer" }}>
                    {initialOptions.length === 0 && (
                        <EuiButtonEmpty style={{ marginTop: "0.5em" }} size="m" iconType="plus" isDisabled={false}>
                            Add {itemType}s
                        </EuiButtonEmpty>
                    )}
                    <EuiFlexItem grow={false}>
                        <EuiFlexGrid gutterSize="s" onClick={toggleEditing}>
                            {initialOptions.map((value, index) => (
                                <EuiFlexItem grow={false} key={index}>
                                    {renderValue ? (
                                        renderValue(value.label)
                                    ) : (
                                        <b>
                                            {value.label}
                                            {index < initialOptions.length - 1 && ", "}
                                        </b>
                                    )}
                                </EuiFlexItem>
                            ))}
                        </EuiFlexGrid>
                    </EuiFlexItem>
                    <EuiFlexItem>
                        {initialOptions.length !== 0 && (
                            <div>
                                <EuiButtonIcon
                                    aria-label="edit"
                                    iconType="pencil"
                                    iconSize="m"
                                    onClick={toggleEditing}
                                />
                            </div>
                        )}
                    </EuiFlexItem>
                </EuiFlexGroup>
            )}
            {_editing && editor}
        </div>
    );
}
export default InlineIconEditor;
